import type { Emitter } from "@core/events/emitter";

/**
 * `IConfirmationModel` — app-level confirmation-dialog queue.
 *
 * Unity has no model for this: any ViewModel calls
 * `Navigate(ViewNames.Confirmation, NavigationMode.Additive,
 * new ConfirmationArgs(...), result => ...)` and the navigation frame pushes
 * the view. Vue has no equivalent frame, so the web needs a seam that models
 * and composables alike can request from — deliberately shaped like the
 * neighbouring {@link IToastsModel} rather than inventing a second pattern.
 *
 * The host ({@link ConfirmationHost}) renders whatever sits at the head of
 * {@link requests}; `request()` resolves with the user's answer, which is the
 * web spelling of Unity's result callback.
 */

/**
 * Port of `Infrastructure/ConfirmationArgs.cs`.
 *
 * **Deviation:** Unity's 2- and 3-argument constructors bake the title and the
 * body into ONE TMP rich-text string (`<b><size=100%>{header1}</size></b> \n\n
 * <alpha=#9F><size=65%>{header2}`). The web keeps them as separate fields and
 * lets CSS do the styling — an `<input>`/DOM tree can't carry inline TMP tags,
 * and the string was only ever a derived presentation detail. Callers that
 * pass just `headerText` get Unity's single-header layout.
 */
export interface ConfirmationArgs {
  /** Title line. Unity `header1` / `HeaderText`. */
  readonly headerText: string;
  /** Optional secondary line under the title. Unity `header2`. */
  readonly detailText?: string;
  /**
   * Confirm-button label. **Empty hides the button** — Unity binds it through
   * `StringEmptyToBoolAdapter`, which is how `("…", "", "OK", "")` renders as
   * an OK-only alert.
   */
  readonly confirmText: string;
  /** Cancel-button label; empty hides it (same adapter as `confirmText`). */
  readonly cancelText: string;
  /**
   * Dismissing without choosing (backdrop click / Esc) resolves `false` when
   * true, and resolves `undefined` when false so the caller can tell "cancel"
   * from "didn't decide" (Unity C3D-3208 — `GoBack()` with no value).
   */
  readonly cancelIfUndecided?: boolean;
  /**
   * When set, renders the "Don't show this again" toggle and persists the key
   * to `localStorage` on any answer (Unity writes `PlayerPrefs`). Callers
   * check {@link IConfirmationModel.isSuppressed} before requesting.
   */
  readonly doNotShowAgainKey?: string;
  /** Optional extra checkbox question; its answer rides on the result. */
  readonly additionalQuestion?: string;
  /** Unity `ConfirmationArgs.Width` (default 320). */
  readonly width?: number;
  /** Unity `ConfirmationArgs.Height` (default 240). */
  readonly height?: number;
  /**
   * `"default"` = `ConfirmationView.prefab`, `"dark"` =
   * `ConfirmationDarkView.prefab`. Unity picks by view name at the call site
   * (`ViewNames.Confirmation` vs `ViewNames.ConfirmationDark`).
   */
  readonly theme?: ConfirmationTheme;
}

export type ConfirmationTheme = "default" | "dark";

/**
 * Unity returns `bool` — or `Tuple<bool,bool>` when an additional question was
 * asked (`ConfirmationViewModel.GetReturnValue`). `confirmed` is `undefined`
 * only for an undecided dismissal with `cancelIfUndecided: false`.
 */
export interface ConfirmationResult {
  readonly confirmed: boolean | undefined;
  readonly additionalAnswer: boolean;
}

/** One queued dialog: the args plus the resolver the host calls. */
export interface ConfirmationRequest {
  readonly id: string;
  readonly args: ConfirmationArgs;
  resolve(result: ConfirmationResult): void;
}

export interface IConfirmationModel {
  readonly onChanged: Emitter<ReadonlyArray<ConfirmationRequest>>;
  readonly requests: ReadonlyArray<ConfirmationRequest>;

  /**
   * Queue a dialog and resolve with the user's answer. Requests are shown one
   * at a time in arrival order — Unity's additive navigation stacks them, but
   * stacked modals read as broken on the web.
   */
  request(args: ConfirmationArgs): Promise<ConfirmationResult>;

  /**
   * Convenience for the overwhelmingly common yes/no case: resolves `true`
   * only on an explicit confirm.
   */
  confirm(args: ConfirmationArgs): Promise<boolean>;

  /** Answer the head request (called by the host). */
  answer(id: string, result: ConfirmationResult): void;

  /** True when the user ticked "Don't show this again" for `key`. */
  isSuppressed(key: string): boolean;

  /** Persist the suppression flag for `key` (Unity `PlayerPrefs.SetInt`). */
  suppress(key: string): void;
}

export const CONFIRMATION_MODEL = Symbol.for("Campfire.ConfirmationModel");
